import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { type Control, Controller, type UseFormSetValue } from 'react-hook-form';

import { CampoListaChips } from '@/ui/components/campo-lista-chips';
import { Button } from '@/ui/primitives/button';
import { Select } from '@/ui/primitives/select';

import { type FormRegra } from './formulario-regra';
import { CONDICAO, TIPOS_CONDICAO, type TipoCondicao } from './textos';

interface Props {
  control: Control<FormRegra>;
  setValue: UseFormSetValue<FormRegra>;
  /** Condições já com valor ao abrir (o editor mostra essas; vazio mostra a de NCM). */
  iniciais: TipoCondicao[];
  erro: string | undefined;
}

function opcoesPara(tipo: TipoCondicao, usados: readonly TipoCondicao[]) {
  return TIPOS_CONDICAO.filter((t) => t === tipo || !usados.includes(t)).map((t) => ({
    valor: t,
    rotulo: CONDICAO[t].opcao,
  }));
}

/**
 * "Quando [NCM começa com ▾] [chips] e [CFOP do fornecedor é ▾] [chips] [+ e outra condição]".
 * Todas as condições informadas precisam casar; dentro de cada uma, qualquer valor da lista.
 */
export function CondicoesEditor({ control, setValue, iniciais, erro }: Props) {
  const [tipos, setTipos] = useState<TipoCondicao[]>(
    iniciais.length > 0 ? iniciais : ['ncmPrefixos'],
  );
  const proximo = TIPOS_CONDICAO.find((t) => !tipos.includes(t));

  const trocar = (indice: number, novo: TipoCondicao) => {
    const antigo = tipos[indice];
    if (!antigo || antigo === novo) return;
    setValue(`condicoes.${antigo}`, [], { shouldDirty: true });
    setTipos(tipos.map((t, i) => (i === indice ? novo : t)));
  };
  const tirar = (tipo: TipoCondicao) => {
    setValue(`condicoes.${tipo}`, [], { shouldDirty: true, shouldValidate: true });
    setTipos(tipos.filter((t) => t !== tipo));
  };

  return (
    <fieldset className="grid gap-2">
      <legend className="sr-only">Condições</legend>
      {tipos.map((tipo, i) => (
        <div key={tipo} className="grid gap-2 sm:grid-cols-[auto_auto_1fr_auto] sm:items-start">
          <span className="text-sm font-medium text-texto-suave sm:w-14 sm:pt-2">
            {i === 0 ? 'Quando' : 'e'}
          </span>
          <Select
            aria-label={`Condição ${String(i + 1)}`}
            className="sm:w-48"
            opcoes={opcoesPara(tipo, tipos)}
            valor={tipo}
            aoMudar={(v) => {
              trocar(i, v as TipoCondicao);
            }}
          />
          <Controller
            control={control}
            name={`condicoes.${tipo}`}
            render={({ field }) => (
              <CampoListaChips
                aria-label={CONDICAO[tipo].opcao}
                {...(erro ? { 'aria-invalid': true as const } : {})}
                valores={field.value ?? []}
                aoMudar={field.onChange}
                interpretar={CONDICAO[tipo].interpretar}
                formatar={CONDICAO[tipo].formatar}
                separador={CONDICAO[tipo].separador}
                mono={CONDICAO[tipo].mono}
                placeholder={CONDICAO[tipo].placeholder}
              />
            )}
          />
          {tipos.length > 1 && (
            <Button
              variante="fantasma"
              tamanho="icone"
              aria-label={`Tirar a condição ${CONDICAO[tipo].opcao}`}
              onClick={() => {
                tirar(tipo);
              }}
            >
              <X aria-hidden />
            </Button>
          )}
        </div>
      ))}
      {erro && <p className="text-xs text-perigo">{erro}</p>}
      {proximo && (
        <Button
          variante="link"
          tamanho="sm"
          className="justify-self-start sm:ml-16"
          onClick={() => {
            setTipos([...tipos, proximo]);
          }}
        >
          <Plus aria-hidden /> e outra condição
        </Button>
      )}
    </fieldset>
  );
}
